import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Sparkles } from 'lucide-react';
import { FramingChat } from '../components/research/FramingChat';
import { PhoneVerifyModal } from '../components/ui';
import { useCreateResearchJob } from '../hooks/useCreateResearchJob';
import { useAuthStore } from '../store/authStore';
import { isTwa } from '../utils/platform';

export function NewResearch() {
  const navigate = useNavigate();
  const location = useLocation();
  // Matter pre-seeded when arriving via "New research in this matter".
  const [matterId, setMatterId] = useState<string | null>(
    (location.state as { matterId?: string } | null)?.matterId ?? null,
  );
  // Seed question when arriving from "Deepen into research" on a draft.
  const prefill = (location.state as { prefill?: string } | null)?.prefill;
  // Document pre-attached when arriving via "Research this document" (§4.5).
  const initialDoc =
    (location.state as { doc?: { id: string; name: string } } | null)?.doc ?? null;
  const user = useAuthStore((s) => s.user);
  const isCheckingAuth = useAuthStore((s) => s.isCheckingAuth);

  const limitReached = !!user && user.monthly_jobs_used >= user.monthly_jobs_limit;

  // Shared creation flow (also used by the global ChatPane): quota errors,
  // the free-tier phone gate, and post-create navigation live in one place.
  const { isSubmitting, error, submit, phoneModal } = useCreateResearchJob();

  return (
    <div className="px-0 py-4 sm:p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 text-legal-500 hover:text-legal-700 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm">Back</span>
      </button>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium text-legal-900">New Legal Research</h1>
          <p className="text-legal-500 mt-1.5 text-[15px]">
            Ask in your own words — the title and the fine print are our job.
          </p>
        </div>
        {/* Quota meter, in words (§4.3) */}
        {user && (
          <p className="flex-shrink-0 text-xs text-legal-500 bg-legal-50 border border-legal-200 rounded-full px-3 py-1.5 whitespace-nowrap mt-1">
            {Math.max(0, user.monthly_jobs_limit - user.monthly_jobs_used)} of{' '}
            {user.monthly_jobs_limit} research{user.monthly_jobs_limit === 1 ? '' : 'es'} left this month
          </p>
        )}
      </div>

      {isCheckingAuth ? (
        <div className="bg-white rounded-2xl border border-legal-200 p-10 text-center text-legal-400 text-sm">
          Loading...
        </div>
      ) : limitReached ? (
        <div className="bg-white rounded-2xl border border-legal-200 p-10 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-amber-50 rounded-full">
              <Lock className="h-6 w-6 text-amber-500" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-legal-900 mb-2">Monthly limit reached</h2>
          <p className="text-legal-500 text-sm mb-6">
            You've used all {user.monthly_jobs_limit} research{user.monthly_jobs_limit === 1 ? '' : 'es'} on your plan this month.
            {isTwa() ? ' Upgrade at urava.app for more.' : ' Upgrade for more.'}
          </p>
          {!isTwa() && (
            <button
              onClick={() => navigate('/pricing')}
              data-track="research-wall-see-plans"
              className="px-6 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors"
            >
              See plans
            </button>
          )}
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-legal-200 p-4 sm:p-6">
            <FramingChat
              isSubmitting={isSubmitting}
              prefill={prefill}
              initialDoc={initialDoc}
              matterId={matterId}
              onMatterChange={setMatterId}
              onAccept={({ question, jurisdiction, law_domain, input_document_id, output_language }) =>
                submit({
                  description: question,
                  input_type: input_document_id ? 'document' : 'text',
                  input_document_id,
                  matter_id: matterId ?? undefined,
                  jurisdiction: jurisdiction || 'All India',
                  law_domain,
                  output_language,
                })
              }
            />
          </div>

          <p className="flex items-center justify-center gap-1.5 text-xs text-legal-400 mt-4">
            <Sparkles className="h-3 w-3 text-accent-500" />
            The memo title is generated for you from your question.
          </p>
        </>
      )}

      <PhoneVerifyModal
        open={phoneModal.open}
        onClose={phoneModal.onClose}
        onVerified={phoneModal.onVerified}
      />
    </div>
  );
}
